import axios from 'axios'
import React, { useState, useEffect } from 'react'
import { ToastContainer } from 'react-toastify'
import { BASE_URL } from '../../config/axios'
import { toast } from 'react-toastify';
import { handleErrorToast } from '../CustomFunctions';
import { AddwithWhiteCircle, UpdatePencil } from '../AllSvg';
import { Modal } from 'react-bootstrap';
import Select from "react-select";



const AddDivision = ({ getDevisionName }) => {
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState(false);
    const [activities, setActivities] = useState([]);

    const [formData, setFormData] = useState({
        division_code: "",
        division_name: "",
        activity_ids: [], // multiple activities
        subactivity_ids: [], // all subactivities of selected activities
    });

    /** ========== Modal Controls ========== */
    const handleShow = () => setShow(true);
    const handleClose = () => {
        setShow(false);
        setErrors({});
        setFormData({
            division_code: "",
            division_name: "",
            activity_ids: [],
            subactivity_ids: [],
        });
    };

    /** ========== Fetch Activities ========== */
    const getActivities = async () => {
        try {
            const result = await axios.get(`${BASE_URL}/detail-design/activity/`);
            setActivities(result.data || []);
        } catch (err) {
            toast.error("Failed to fetch activities");
        }
    };

    useEffect(() => {
        getActivities();
    }, []);

    /** ========== Handle Input Changes ========== */
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    /** ========== Handle Activity Selection (Multiple) ========== */
    const handleActivitySelect = (selectedOptions) => {
        if (!selectedOptions || selectedOptions.length === 0) {
            setFormData((prev) => ({
                ...prev,
                activity_ids: [],
                subactivity_ids: [],
            }));
            return;
        }

        // Extract selected activity IDs
        const selectedActivityIds = selectedOptions.map((option) => option.value);

        // Collect all subactivity IDs from selected activities
        const selectedSubactivityIds = activities
            .filter((act) => selectedActivityIds.includes(act.id))
            .flatMap((act) => act.subactivities?.map((sub) => sub.id) || []);

        setFormData((prev) => ({
            ...prev,
            activity_ids: selectedActivityIds,
            subactivity_ids: selectedSubactivityIds,
        }));
    };

    /** ========== Validate Form ========== */
    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["division_code", "division_name", "activity_ids"];

        requiredFields.forEach((field) => {
            if (!formData[field] || (Array.isArray(formData[field]) && formData[field].length === 0)) {
                newErrors[field] = `${field.replace("_", " ")} is required!`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /** ========== Submit Form ========== */
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        const loadingToastId = toast.loading("Adding division...");

        try {
            const payload = {
                division_code: formData.division_code,
                division_name: formData.division_name,
                activity_ids: formData.activity_ids, // multiple activity IDs
                created_at: new Date().toISOString(),
            };

            const res = await axios.post(`${BASE_URL}/detail-design/division/`, payload);

            if (res.status === 201 || res.status === 200) {
                toast.success("Division added successfully!");
                handleClose();
                getDevisionName();
            }
        } catch (err) {
            console.error("Error adding division:", err.response?.data || err.message);
            toast.error(err.response?.data?.message || "Something went wrong!");
        } finally {
            toast.dismiss(loadingToastId);
        }
    };

    /** ========== Activity Dropdown Options ========== */
    const activityOptions = activities
        ?.sort((a, b) => parseInt(a.activity_code) - parseInt(b.activity_code))
        .map((activity) => ({
            value: activity.id,
            label: `${activity.activity_code} - ${activity.activity_name}`,
        }));

    return (
        <>
            <button title="Add Division" className="upload-svg" onClick={handleShow}>
                <AddwithWhiteCircle /> Division
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave">
                <Modal.Header closeButton className="justify-center task-Tab-heading font-weight500 font-size-heading">
                    <Modal.Title>Add Division</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <form className="register-form" onSubmit={handleFormSubmit}>
                        <div className="form-flex-wrap">
                            {/* Division Code */}
                            <div className="flex-column">
                                <label className="form-labels">
                                    Division Code <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength={100}
                                    name="division_code"
                                    placeholder="Division code"
                                    onChange={handleInputChange}
                                    value={formData.division_code}
                                    className={`form-input ${errors.division_code ? "input-error" : ""}`}
                                />
                                {errors.division_code && <span className="error-message">{errors.division_code}</span>}
                            </div>

                            {/* Division Name */}
                            <div className="flex-column">
                                <label className="form-labels">
                                    Division Name <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength={100}
                                    name="division_name"
                                    placeholder="Division name"
                                    onChange={handleInputChange}
                                    value={formData.division_name}
                                    className={`form-input ${errors.division_name ? "input-error" : ""}`}
                                />
                                {errors.division_name && <span className="error-message">{errors.division_name}</span>}
                            </div>

                            {/* Activity Dropdown (Multiple Selection) */}
                            <div className="flex-column">
                                <label className="form-labels">
                                    Select Activities <span className="required">*</span>
                                </label>
                                <Select
                                    className="form-input"
                                    options={activityOptions}
                                    onChange={handleActivitySelect}
                                    value={activityOptions.filter((opt) => formData.activity_ids.includes(opt.value))}
                                    isClearable
                                    isMulti // ✅ Allow multiple selections
                                />
                                {errors.activity_ids && <span className="error-message">{errors.activity_ids}</span>}
                            </div>

                            {/* Auto Display Sub-Activities */}
                            {formData.subactivity_ids.length > 0 && (
                                <div className="flex-column">
                                    <label className="form-labels">Subactivities</label>
                                    <ul className="subactivity-list">
                                        {activities
                                            .filter((act) => formData.activity_ids.includes(act.id))
                                            .flatMap((act) => act.subactivities || [])
                                            .map((sub) => (
                                                <li key={sub.id}>
                                                    {sub.subactivity_code} - {sub.subactivity_name}
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            )}

                            {/* Submit Button */}
                            <div className="button-models" style={{ width: "100%" }}>
                                <button type="submit" className="model-button font-weight500 model-button-submit">
                                    Add
                                </button>
                            </div>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default AddDivision;


const UpdateDivision = ({ item, getDevisionName }) => {
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState(false);
    const [Activity, setActivity] = useState([]);

    const [formData, setFormData] = useState({
        division_code: "",
        division_name: "",
        activity_code: "",
        activity_name: "",
        subactivity_ids: [],
    });

    /** ========== Open Modal and Load Data ========== */
    const handleShow = () => {
        if (item) {
            setFormData({
                id: item.id || '',
                division_code: item.division_code || '',
                division_name: item.division_name || '',
                activity_code: item.activity_code || '',
                activity_name: item.activity_name || '',
                subactivity_ids: item.subactivity_ids || [],
            });
        }
        setShow(true);
    };

    /** ========== Close Modal and Reset Form ========== */
    const handleClose = () => {
        setShow(false);
        setFormData({
            id: '',
            division_code: "",
            division_name: "",
            activity_code: "",
            activity_name: "",
            subactivity_ids: [],
        });
        setErrors({});
    };

    /** ========== Fetch Activities Once ========== */
    useEffect(() => {
        getActivities();
    }, []);

    const getActivities = async () => {
        try {
            const result = await axios.get(`${BASE_URL}/detail-design/activity/`);
            setActivity(result.data || []);
        } catch (err) {
            console.error("Error fetching activities:", err);
            toast.error("Failed to fetch activities");
        }
    };

    /** ========== Handle Input Change ========== */
    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    /** ========== Handle Multi-Select Sub-Activities ========== */
    const selectSubActivity = (selected) => {
        const subactivity_ids = selected?.map((item) => item.value) || [];
        setFormData((prev) => ({
            ...prev,
            subactivity_ids,
        }));
    };

    /** ========== Form Submit ========== */
    const handleFormSubmit = async (e) => {
        e.preventDefault();

        const loadingToastId = toast.loading("Updating Division...");

        try {
            let res = await axios.put(
                `${BASE_URL}/detail-design/division/${item.id}/`,
                {
                    division_code: formData.division_code,
                    division_name: formData.division_name,
                    activity_ids: [formData.activity_code], // If you need multiple, adjust this
                    subactivity_ids: formData.subactivity_ids,
                }
            );

            if (res.status === 200 || res.status === 201) {
                toast.success("Division updated successfully!");
                setShow(false);
                getDevisionName(); // ✅ Refresh table
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to update division");
        } finally {
            toast.dismiss(loadingToastId);
        }
    };

    /** ========== Map Activity Options for Multi-Select ========== */
    const options = Activity
        ?.sort((a, b) => parseInt(a.activity_code) - parseInt(b.activity_code))
        .map((e) => ({
            value: e.id,
            label: `${e.activity_code} - ${e.activity_name}`,
        }));

    return (
        <>
            <button onClick={handleShow} className="model-edit-button">
                <UpdatePencil />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave" restoreFocus={false}>
                <Modal.Header closeButton className="justify-center task-Tab-heading font-weight500 font-size-heading">
                    <Modal.Title>Update Division</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form className="register-form" onSubmit={handleFormSubmit}>
                        <div className="form-flex-wrap">
                            {/* Division Code */}
                            <div className="flex-column">
                                <label className="form-labels">
                                    Division Code <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="division_code"
                                    value={formData.division_code}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    placeholder="Division Code"
                                />
                            </div>

                            {/* Division Name */}
                            <div className="flex-column">
                                <label className="form-labels">
                                    Division Name <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="division_name"
                                    value={formData.division_name}
                                    onChange={handleInputChange}
                                    className="form-input"
                                    placeholder="Division Name"
                                />
                            </div>

                            {/* Sub-Activity Multi-Select */}
                            <div className="flex-column form-group-select">
                                <label className="form-labels">
                                    Select Activities: <span className="required">*</span>
                                </label>
                                <Select
                                    className="form-input"
                                    options={options}
                                    isMulti
                                    onChange={selectSubActivity}
                                    value={(formData.subactivity_ids || []).map((id) => ({
                                        value: id,
                                        label: options.find((opt) => opt.value === id)?.label || id,
                                    }))}
                                />
                            </div>

                            {/* Submit Button */}
                            <div className="button-models" style={{ width: '100%' }}>
                                <button type="submit" className="model-button font-weight500 model-button-submit">
                                    Update
                                </button>
                            </div>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
};


const DeleteDivision = ({ item, getActivity }) => {
    const [show, setShow] = useState(false);

    const handleShow = () => {
        setShow(true)
    }

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();
        const loadingToastId = toast.loading("Loading: Please wait...");
        try {
            let res = await axios.delete(`${BASE_URL}/detail-design/division/${item.id}/`)
            if (res.status === 200) {
                toast.success(`${item.division_name} deleted successfully`);
                await getActivity(); // refresh table
                setShow(false);
            }

        } catch (err) {
            toast.error("Failed to delete division");
        } finally {
            toast.dismiss(loadingToastId);
        }
    }


    return (
        <>
            <button
                title="Delete Insurance"
                className="model-delete-button"
                onClick={handleShow}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                >
                    <path
                        d="M15.1875 3.375H12.375V2.8125C12.375 2.36495 12.1972 1.93572 11.8807 1.61926C11.5643 1.30279 11.1351 1.125 10.6875 1.125H7.3125C6.86495 1.125 6.43572 1.30279 6.11926 1.61926C5.80279 1.93572 5.625 2.36495 5.625 2.8125V3.375H2.8125C2.66332 3.375 2.52024 3.43426 2.41475 3.53975C2.30926 3.64524 2.25 3.78832 2.25 3.9375C2.25 4.08668 2.30926 4.22976 2.41475 4.33525C2.52024 4.44074 2.66332 4.5 2.8125 4.5H3.375V14.625C3.375 14.9234 3.49353 15.2095 3.7045 15.4205C3.91548 15.6315 4.20163 15.75 4.5 15.75H13.5C13.7984 15.75 14.0845 15.6315 14.2955 15.4205C14.5065 15.2095 14.625 14.9234 14.625 14.625V4.5H15.1875C15.3367 4.5 15.4798 4.44074 15.5852 4.33525C15.6907 4.22976 15.75 4.08668 15.75 3.9375C15.75 3.78832 15.6907 3.64524 15.5852 3.53975C15.4798 3.43426 15.3367 3.375 15.1875 3.375ZM6.75 2.8125C6.75 2.66332 6.80926 2.52024 6.91475 2.41475C7.02024 2.30926 7.16332 2.25 7.3125 2.25H10.6875C10.8367 2.25 10.9798 2.30926 11.0852 2.41475C11.1907 2.52024 11.25 2.66332 11.25 2.8125V3.375H6.75V2.8125ZM13.5 14.625H4.5V4.5H13.5V14.625ZM7.875 7.3125V11.8125C7.875 11.9617 7.81574 12.1048 7.71025 12.2102C7.60476 12.3157 7.46168 12.375 7.3125 12.375C7.16332 12.375 7.02024 12.3157 6.91475 12.2102C6.80926 12.1048 6.75 11.9617 6.75 11.8125V7.3125C6.75 7.16332 6.80926 7.02024 6.91475 6.91475C7.02024 6.80926 7.16332 6.75 7.3125 6.75C7.46168 6.75 7.60476 6.80926 7.71025 6.91475C7.81574 7.02024 7.875 7.16332 7.875 7.3125ZM11.25 7.3125V11.8125C11.25 11.9617 11.1907 12.1048 11.0852 12.2102C10.9798 12.3157 10.8367 12.375 10.6875 12.375C10.5383 12.375 10.3952 12.3157 10.2898 12.2102C10.1843 12.1048 10.125 11.9617 10.125 11.8125V7.3125C10.125 7.16332 10.1843 7.02024 10.2898 6.91475C10.3952 6.80926 10.5383 6.75 10.6875 6.75C10.8367 6.75 10.9798 6.80926 11.0852 6.91475C11.1907 7.02024 11.25 7.16332 11.25 7.3125Z"
                        fill="#F72B50"
                    />
                </svg>
            </button>
            <Modal
                show={show}
                onHide={() => setShow(false)}
                dialogClassName="request-leave width-40vw"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Delete Activity: {item.division_name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to delete{" "}
                        <strong>{item.division_name}</strong>?
                    </p>
                    <div className="button-models">
                        <button
                            className="model-button model-button-cancel"
                            onClick={() => setShow(false)}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleFormSubmitDelete}
                            className="model-button model-button-delete"
                        >
                            Delete
                        </button>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    )

}




export { AddDivision, DeleteDivision, UpdateDivision }